import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faInfoCircle, faPaperclip, faExclamationCircle, faSpinner, faUserPlus, faUserMinus, faCarCrash, faUsers } from '@fortawesome/free-solid-svg-icons';
import DeleteTimelineEventButton from '../../../common/DeleteTimelineEventButton';
const { rootUrl } = require('../../../../config/config');

const iconForType = (type) => {
    if (!type) return faInfoCircle;
    const t = type.toLowerCase();
    if (t.includes('inspect')) return faCalendarAlt;
    if (t.includes('attach') || t.includes('file')) return faPaperclip;
    if (t.includes('alert') || t.includes('warning')) return faExclamationCircle;
    if (t.includes('hiring')) return faUserPlus;
    if (t.includes('termination')) return faUserMinus;
    if (t.includes('next_of_kin')) return faUsers;
    if (t.includes('accident') || t.includes('incident')) return faCarCrash;
    return faInfoCircle;
};

const formatDate = (iso) => {
    try {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleString();
    } catch (e) {
        return iso;
    }
};

const formatMonthDay = (iso) => {
    try {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
        return iso;
    }
};

const formatTime = (iso) => {
    try {
        if (!iso) return '';
        const d = new Date(iso);
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch (e) {
        return iso;
    }
};

const DriverTimeline = ({ driverId }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const removeEvent = (id) => setEvents((prev) => prev.filter((e) => e.id !== id));

    useEffect(() => {
        if (!driverId) return;

        const token = localStorage.getItem('authToken');
        
        const url = `${rootUrl.replace(/\/$/, '')}/drivers/${driverId}/timeline`;

        setLoading(true);
        fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: token ? `Bearer ${token}` : ''
            }
        })
            .then((res) => {
                if (!res.ok) throw new Error('Failed to fetch timeline events');
                return res.json();
            })
            .then((data) => {
                const list = Array.isArray(data) ? data : data.data || [];
                list.sort((a, b) => new Date(b.event_date || b.created_at) - new Date(a.event_date || a.created_at));
                setEvents(list);
                setError(null);
            })
            .catch((err) => {
                setError(err.message || 'Error fetching timeline');
                setEvents([]);
            })
            .finally(() => setLoading(false));
    }, [driverId]);

    return (
        <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-[#2a2e30]">Driver Timeline</h3>
                <div className="text-sm text-gray-500">Showing activity history</div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-[#FF5733] mr-3" />
                        <span className="text-gray-600">Loading timeline...</span>
                    </div>
                ) : error ? (
                    <div className="text-center py-8 text-red-500">
                        <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
                        {error}
                    </div>
                ) : events.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-4xl mb-3 text-gray-300" />
                        <p>No timeline events recorded yet.</p>
                    </div>
                ) : (
                    <div className="relative pl-4 border-l-2 border-gray-200 space-y-8">
                        {events.map((event, index) => (
                            <div key={event.id || index} className="relative">
                                <div className="absolute -left-[25px] bg-white p-1 rounded-full border border-gray-200">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                        event.event_type === 'warning' ? 'bg-red-100 text-red-600' :
                                        event.event_type === 'note' ? 'bg-blue-100 text-blue-600' :
                                        'bg-gray-100 text-gray-600'
                                    }`}>
                                        <FontAwesomeIcon icon={iconForType(event.event_type)} className="text-sm" />
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <h4 className="font-semibold text-gray-800">{event.title}</h4>
                                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 uppercase tracking-wide">
                                                {event.event_type}
                                            </span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <div className="text-right">
                                                <div className="text-sm font-bold text-gray-700">{formatMonthDay(event.event_date)}</div>
                                                <div className="text-xs text-gray-500">{formatTime(event.event_date)}</div>
                                            </div>
                                            {event.id && (
                                                <DeleteTimelineEventButton
                                                    deleteUrl={`${rootUrl.replace(/\/$/, '')}/drivers/timeline/${event.id}`}
                                                    eventTitle={event.title}
                                                    onDeleted={() => removeEvent(event.id)}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    {event.description && (
                                        <p className="text-gray-600 text-sm mb-3 whitespace-pre-wrap">{event.description}</p>
                                    )}
                                    <div className="flex items-center justify-between text-xs text-gray-400 border-t border-gray-50 pt-2 mt-2">
                                        <span>By: {event.created_by || 'System'}</span>
                                        <span>{formatDate(event.created_at)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DriverTimeline;
